import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ContactMemberSchema, contactMemberSchema } from "@msuite/utils/schemas";
import type { Nullable, Tables } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { ContactMemberForm } from "@/forms/contact-member-form";
import { client } from "@/hono";

/** Props Interface */
interface AddContactMemberModalProps extends ModalProps {
	contactId: string | null;
	onComplete: (contactMember: Nullable<Tables<"contactMembers">>) => Promise<void> | void;
}

export function AddContactMemberModal({
	isOpen,
	onClose,
	contactId,
	onComplete,
}: AddContactMemberModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		defaultValues: async () => {
			const defaultValues: Partial<ContactMemberSchema> = {
				title: "mr",
			};
			if (contactId) {
				defaultValues.contactId = contactId;
			}
			return defaultValues as ContactMemberSchema;
		},
		resolver: resolver(contactMemberSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: ContactMemberSchema) {
		try {
			const response = await client.contacts[":contactId"].members.$post({
				param: {
					contactId: contactId || json.contactId || "null",
				},
				json,
			});
			const data = await responseHandler(response);
			await onComplete(data.contactMember);
			handleOnClose();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnClose() {
		onClose();
		methods.reset();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={handleOnClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				title={t("contacts.add_member")}
				data-cy="contact-member-form"
			>
				<Modal.Body>
					<ContactMemberForm isContactPickerDisabled={!!contactId} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
